<template>
  <div class="w-96 space-y-2 font-intervariable relative overflow-hidden">
    <!-- Main list view -->
    <div 
      class="transition-all duration-300 ease-in-out"
      :class="selectedMessage ? '-translate-x-10 opacity-0 pointer-events-none' : 'translate-x-0 opacity-100 pointer-events-auto'"
    >
      <!-- Header with tabs -->
      <div class="space-y-4 p-8">
        <div class="flex gap-1">
          <button
            v-for="tab in tabs" 
            :key="tab.id"
            @click="setActiveTab(tab.id)"
            class="relative px-3 py-1.5 text-xs font-medium transition-all duration-200 rounded-full flex-1 text-center"
            :class="[
              activeTab === tab.id
                ? 'text-background bg-muted-foreground shadow-sm' 
                : 'text-muted-foreground hover:text-foreground/80'
            ]"
          >
            <div class="flex items-center justify-center gap-1">
              <span>{{ tab.label }}</span>
              <Badge 
                v-if="tab.count > 0"
                :variant="activeTab === tab.id ? 'secondary' : 'outline'"
                :class="[
                  'text-[10px] py-0 px-1 ml-1 border-none ring-0 min-w-[16px] h-4 flex items-center justify-center',
                  tab.id === 'inbox' && unreadCount > 0 
                    ? 'bg-blue-400 text-white' 
                    : 'bg-background text-primary'
                ]"
              >
                {{ tab.count }}
              </Badge>
            </div>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="space-y-4 pr-2 pb-2">
        <ScrollArea class="h-64 px-1 p-2">
          <div v-if="isLoading" class="flex items-center justify-center py-8">
            <Loader2 class="w-4 h-4 animate-spin mr-2" />
            <span class="text-xs text-muted-foreground">Loading messages...</span>
          </div>

          <div v-else-if="currentMessages.length === 0" class="flex flex-col items-center justify-center py-8 text-center">
            <Icon 
              :name="activeTab === 'inbox' ? 'lucide:inbox' : 'lucide:archive'" 
              class="w-8 h-8 text-muted-foreground mb-2" 
            />
            <p class="text-xs text-muted-foreground">
              {{ activeTab === 'inbox' ? 'No new messages' : 'No archived messages' }}
            </p>
          </div>

          <div v-else class="space-y-1 p-1">
            <MessageItem
              v-for="message in currentMessages"
              :key="message.id"
              :message="message"
              @mark-as-read="markAsRead"
              @archive="archiveMessage"
              @unarchive="unarchiveMessage"
              @message-click="handleMessageClick"
            />
          </div>
        </ScrollArea>

        <!-- Footer with actions -->
        <div v-if="activeTab === 'inbox' && unreadCount > 0" class="pt-4 border-t border-muted/20">
          <Button 
            variant="ghost" 
            size="sm" 
            class="w-full text-xs h-7 hover:bg-muted/40 rounded-xl my-2"
            @click="handleMarkAllAsRead"
          >
            <Icon name="lucide:check-check" class="w-3 h-3 mr-1" />
            Mark all as read
          </Button>
        </div>
      </div>
    </div>

    <!-- Single message view -->
    <div 
      class="absolute inset-0 transition-all duration-300 ease-in-out"
      :class="selectedMessage ? 'translate-x-0 opacity-100 pointer-events-auto' : 'translate-x-10 opacity-0 pointer-events-none'"
    >
      <div v-if="selectedMessage" class="h-full flex flex-col">
        <!-- Header with back button -->
        <div class="flex items-center gap-3 p-4 border-b border-muted/20">
          <Button
            variant="text"
            size="sm"
            class="h-8 w-8 p-0"
            @click="goBack"
          >
            <Icon name="lucide:chevron-left" class="w-4 h-4" />
          </Button>
          <h3 class="text-sm font-medium">{{ selectedMessage.title }}</h3>
        </div>

        <!-- Message content -->
        <div class="flex-1 flex flex-col">
          
            <!-- Content with consistent styling -->
            <div class="py-4">
              <div class="flex items-start gap-2 pr-2">
                
                <!-- Icon matching MessageItem style -->
                <div class="flex-shrink-0 -mt-1 pl-6">
                  <Icon 
                    :name="getMessageIcon(selectedMessage.message_type)" 
                    class="w-4 h-4 relative -top-0.5"
                    :class="[
                      !selectedMessage.is_read ? 'opacity-100' : 'opacity-60'
                    ]"
                  />
                </div>
                <ScrollArea class="h-[180px] pr-4" type="hover">
                <!-- Content -->
                
                <div class="flex-1 min-w-0 -top-1 relative">
                  <p class="text-[0.85em] leading-relaxed whitespace-pre-wrap pl-[0.8rem] text-muted-foreground">
                    {{ selectedMessage.content }}
                  </p>
                </div>
              </ScrollArea>
              </div>
            </div>

          <!-- Action button -->
          <div v-if="selectedMessage.action_url && selectedMessage.action_label" class="px-6 pb-3 pt-1 flex justify-end">
            <Button 
              size="sm"
              class="rounded-xl"
              @click="handleActionClick(selectedMessage)"
            >
              {{ selectedMessage.action_label }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-vue-next'
import MessageItem from './MessageItem.vue'
import { useMessages } from '@/composables/useMessages'
import type { UserMessage } from '@/stores/messageStore'

const emit = defineEmits(['close'])

const {
  inboxMessages,
  archivedMessages,
  unreadCount,
  isLoading,
  activeTab,
  setActiveTab,
  markAsRead,
  archiveMessage,
  unarchiveMessage,
  markAllAsRead,
  getMessageIcon,
  getPriorityColor,
  formatTimeAgo
} = useMessages()

// Single message view state
const selectedMessage = ref<UserMessage | null>(null)

const tabs = computed(() => [
  {
    id: 'inbox' as const,
    label: 'Inbox',
    count: inboxMessages.value.length
  },
  {
    id: 'archived' as const,
    label: 'Archived',
    count: archivedMessages.value.length
  }
])

const currentMessages = computed(() => {
  return activeTab.value === 'inbox' ? inboxMessages.value : archivedMessages.value
})

onMounted(() => {
  activeTab.value = 'inbox'
})

// Handle message click to show single view
const handleMessageClick = async (message: UserMessage) => {
  // Mark as read if not already read
  if (!message.is_read) {
    await markAsRead(message.id)
  }
  selectedMessage.value = message
}

// Go back to message list
const goBack = () => {
  selectedMessage.value = null
}

// Handle action button click in single message view
const handleActionClick = (message: UserMessage) => {
  if (message.action_url) {
    window.open(message.action_url, '_blank')
  }
}

// Handle mark all as read with optional auto-close
const handleMarkAllAsRead = async () => {
  await markAllAsRead()
  // Optional: Auto-close popover after marking all as read
  // setTimeout(() => emit('close'), 500)
}
</script> 